import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { ArrowLeft, Gift, Loader2, CheckCircle, MapPin, AlertCircle, Copy, Check, ShoppingBag, Lock, Star, ShoppingCart, Plus, Minus, Trash2, FlaskConical } from "lucide-react";
import { toTitleCase } from "@/lib/utils";

const CART_STORAGE_KEY = "myerba_redeem_cart_v1";
const CART_MAX_LINES = 5;
const CART_MAX_QTY = 25;

// Resolve an admin-uploaded asset URL (segment/instrument/reagent logos) —
// supports absolute URLs and backend-relative paths like "/api/files/...".
const resolveImg = (url) =>
  url ? (url.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL || ""}${url}`) : "";

// Normalize a segment name to its admin asset key slug, e.g.
// "Clinical Chemistry" -> "clinical_chemistry" (asset key "segment_clinical_chemistry").
const segmentSlug = (name) => (name || "").toLowerCase().trim().replace(/\s+/g, "_");

// Reagent thumbnail with a graceful reagent-flask icon fallback when no image
// is mapped (or the image fails to load).
const ReagentImg = ({ url, fallbackUrl, alt, iconClass = "w-6 h-6", testid }) => {
  const [err, setErr] = useState(false);
  const [fbErr, setFbErr] = useState(false);
  const prefix = (u) => (u && (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL || ""}${u}`)) || null;
  const src = url && !err ? prefix(url) : null;
  if (src) {
    return (
      <img
        src={src}
        alt={alt || "reagent"}
        className="w-full h-full object-contain"
        onError={() => setErr(true)}
        data-testid={testid}
      />
    );
  }
  // Fall back to the admin-managed global reagent icon (Brand Assets → icon_reagent)
  const fbSrc = fallbackUrl && !fbErr ? prefix(fallbackUrl) : null;
  if (fbSrc) {
    return (
      <img
        src={fbSrc}
        alt={alt || "reagent"}
        className="w-full h-full object-contain"
        onError={() => setFbErr(true)}
        data-testid={testid ? `${testid}-asset` : undefined}
      />
    );
  }
  return <FlaskConical className={`${iconClass} text-[#008A99]`} data-testid={testid ? `${testid}-icon` : undefined} />;
};

export default function RedeemPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState("segments"); // segments → instruments → reagents → checking → distributor → confirm → success → history
  const [products, setProducts] = useState([]);
  const [allInstruments, setAllInstruments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState("");
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [radius, setRadius] = useState(12);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [redemptions, setRedemptions] = useState([]);
  const [distLoading, setDistLoading] = useState(false);
  const [inventoryOk, setInventoryOk] = useState(false);
  // Recommended-dealer state (most-transacted dealer for this customer, with nearest fallback).
  const [recommended, setRecommended] = useState(null); // { distributor, source }
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState("");
  const [showAllDealers, setShowAllDealers] = useState(false);
  // Multi-item cart state — persisted in sessionStorage so the basket survives
  // navigating across segments/instruments while building the order.
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(CART_STORAGE_KEY) || "[]"); } catch { return []; }
  });
  const [cartError, setCartError] = useState("");
  // Admin-managed segment logos (from /assets?category=segment), keyed by slug.
  const [segmentAssets, setSegmentAssets] = useState({});
  // Admin-managed global reagent icon (Brand Assets → icon_reagent), used as the
  // thumbnail fallback for reagents that don't have their own image.
  const [reagentIcon, setReagentIcon] = useState("");
  // Index into the ranked dealer list (0 = best match); "Change" cycles this.
  const [dealerIdx, setDealerIdx] = useState(0);
  // Point 24 — reagents the customer has already scanned are hidden from Redeem.
  const [scannedCodes, setScannedCodes] = useState(() => new Set());

  const balance = user?.balance_points || 0;

  // Persist cart on every change
  useEffect(() => {
    try { sessionStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart)); } catch { /* ignore quota errors */ }
  }, [cart]);

  useEffect(() => {
    Promise.all([
      API.get("/instruments").catch(() => ({ data: [] })),
      API.get("/products?limit=1000").catch(() => ({ data: { products: [] } })),
      API.get("/assets").catch(() => ({ data: [] })),
      API.get("/qr/scanned-product-codes").catch(() => ({ data: { product_codes: [] } })),
    ]).then(([instRes, prodRes, assetRes, scanRes]) => {
      setAllInstruments(instRes.data || []);
      setProducts(prodRes.data?.products || []);
      setScannedCodes(new Set(scanRes.data?.product_codes || []));
      const map = {};
      let rxIcon = "";
      (assetRes.data || []).forEach(a => {
        const key = a.key || "";
        if (key === "icon_reagent") { rxIcon = a.image_url || ""; return; }
        if (a.image_url && key.startsWith("segment_")) map[key.replace(/^segment_/, "")] = a.image_url;
      });
      setSegmentAssets(map);
      setReagentIcon(rxIcon);
    }).finally(() => setLoading(false));
  }, []);

  // Reagents (products) filtered by selected instrument, sorted alphabetically by name.
  // Point 24: exclude any reagent the customer has already scanned.
  const reagentsForInstrument = selectedInstrument
    ? products.filter(p => {
        const codes = (p.instrument_codes && p.instrument_codes.length ? p.instrument_codes : [p.instrument_code]).filter(Boolean);
        return codes.includes(selectedInstrument.instrument_code) && !scannedCodes.has(p.product_code);
      }).sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }))
    : [];

  // Segments derived from loaded instruments (in catalog order)
  const segmentOrder = ["Clinical Chemistry", "Hematology", "Critical Care", "Urinalysis", "Hemostasis", "ESR", "Diabetes Management", "Immunology"];
  const segments = (() => {
    const present = new Set(allInstruments.map(i => i.segment).filter(Boolean));
    const ordered = segmentOrder.filter(s => present.has(s));
    for (const s of present) { if (!ordered.includes(s)) ordered.push(s); }
    return ordered;
  })();
  const instrumentsForSegment = selectedSegment
    ? allInstruments.filter(i => i.segment === selectedSegment)
    : [];

  const loadRedemptions = () => { API.get("/redemptions").then(r => setRedemptions(r.data || [])).catch(() => {}); };

  // Auto-open the history view when called with ?view=history (Profile → My Redemptions).
  useEffect(() => {
    if (searchParams.get("view") !== "history") return;
    (async () => {
      setStep("history");
      loadRedemptions();
    })();
  }, [searchParams]);

  const checkInventory = async (product) => {
    setSelectedProduct(product);
    setStep("checking");
    setError("");
    try {
      const { data } = await API.post("/redemptions/check-inventory", { product_code: product.product_code });
      if (data.available) {
        setInventoryOk(true);
        setStep("distributor");
        loadRecommendedDealer();
      } else {
        setError("This item is currently out of stock. Please try again later.");
        setStep("reagents");
      }
    } catch (e) {
      setInventoryOk(true);
      setStep("distributor");
      loadRecommendedDealer();
    }
  };

  const loadRecommendedDealer = async () => {
    setRecLoading(true);
    setRecError("");
    setRecommended(null);
    setSelectedDistributor(null);
    setShowAllDealers(false);
    try {
      let url = "/distributors/recommended";
      if (user?.last_lat && user?.last_long) url += `?lat=${user.last_lat}&lng=${user.last_long}`;
      const { data } = await API.get(url);
      if (data?.distributor) {
        setRecommended(data);
        setDealerIdx(0);
        setSelectedDistributor(data.distributor);
      } else {
        setRecError("No dealers available right now.");
      }
    } catch (e) {
      setRecError(e.response?.data?.detail || "Could not load a dealer.");
    } finally {
      setRecLoading(false);
    }
  };

  const loadDistributors = async (r) => {
    setDistLoading(true);
    try {
      let url = `/distributors/search?limit=30&radius_km=${r}`;
      if (user?.last_lat && user?.last_long) url += `&lat=${user.last_lat}&lng=${user.last_long}`;
      const { data } = await API.get(url);
      setDistributors(data || []);
    } catch { /* ignore */ } finally { setDistLoading(false); }
  };

  const handleRadiusChange = (r) => { setRadius(r); loadDistributors(r); };

  // Ranked dealer list: best match + ONE alternate only. "Change" toggles
  // between the best dealer and the single next-best alternative.
  const rankedDealers = (recommended?.distributor
    ? [recommended.distributor, ...(recommended.alternatives || [])]
    : []).slice(0, 2);
  const currentDealer = rankedDealers[dealerIdx] || recommended?.distributor || null;
  const cycleDealer = () => {
    if (rankedDealers.length < 2) return;
    const next = (dealerIdx + 1) % rankedDealers.length;
    setDealerIdx(next);
    setSelectedDistributor(rankedDealers[next]);
  };

  const REDEMPTION_POINTS_PER_REAGENT = 20; // legacy fallback

  // Points required = customer_price (1 point = ₹1). Falls back to flat 20 only if missing.
  const getPointsRequired = (p) => {
    const v = p?.customer_price ?? p?.points_to_redeem ?? p?.mrp;
    return v && v > 0 ? Math.round(Number(v)) : REDEMPTION_POINTS_PER_REAGENT;
  };
  const canRedeem = (p) => balance >= getPointsRequired(p) && getPointsRequired(p) > 0;

  // ── Cart helpers ─────────────────────────────────────────────────
  const cartTotal = cart.reduce((sum, line) => sum + line.unit_points * line.quantity, 0);
  const cartItemCount = cart.reduce((n, line) => n + line.quantity, 0);
  const findCartLine = (code) => cart.find(l => l.product_code === code);

  const addToCart = (product) => {
    setCartError("");
    if (product.not_eligible) { setCartError(product.ineligible_reason || "Product is not eligible for discounting."); return; }
    const pts = getPointsRequired(product);
    if (pts <= 0) { setCartError(`${product.name || product.product_code} is not redeemable.`); return; }
    setCart(prev => {
      const existing = prev.find(l => l.product_code === product.product_code);
      if (existing) {
        if (existing.quantity >= CART_MAX_QTY) { setCartError(`Max ${CART_MAX_QTY} per item.`); return prev; }
        return prev.map(l => l.product_code === product.product_code ? { ...l, quantity: l.quantity + 1 } : l);
      }
      if (prev.length >= CART_MAX_LINES) { setCartError(`Cart can hold a maximum of ${CART_MAX_LINES} different reagents.`); return prev; }
      return [...prev, {
        product_code: product.product_code,
        name: product.name || product.product_code,
        pack_size: product.pack_size || "",
        unit_points: pts,
        quantity: 1,
        image_url: product.image_url || "",
      }];
    });
  };

  const updateCartQty = (code, delta) => {
    setCartError("");
    setCart(prev => prev.flatMap(l => {
      if (l.product_code !== code) return [l];
      const next = l.quantity + delta;
      if (next <= 0) return [];
      if (next > CART_MAX_QTY) { setCartError(`Max ${CART_MAX_QTY} per item.`); return [l]; }
      return [{ ...l, quantity: next }];
    }));
  };

  const removeFromCart = (code) => {
    setCartError("");
    setCart(prev => prev.filter(l => l.product_code !== code));
  };

  const clearCart = () => { setCart([]); setCartError(""); };

  const beginCheckout = async () => {
    if (cart.length === 0) return;
    if (cartTotal > balance) { setCartError(`Cart total ${cartTotal} pts exceeds your balance (${balance.toLocaleString()} pts).`); return; }
    setStep("distributor");
    loadRecommendedDealer();
  };

  // ── Single-item path (used by the Test Redemption tile only) ─────
  const confirmRedeem = async () => {
    if (!selectedProduct || !selectedDistributor) return;
    setLoading(true); setError("");
    try {
      const pts = getPointsRequired(selectedProduct);
      const { data } = await API.post("/redemptions/submit", {
        product_code: selectedProduct.product_code,
        product_description: selectedProduct.name,
        product_quantity: 1,
        product_value: pts,
        distributor_id: selectedDistributor.db_code || "",
        distributor_name: selectedDistributor.name || ""
      });
      setResult(data);
      setStep("success");
      refreshUser();
    } catch (e) { setError(e.response?.data?.detail || "Redemption failed"); }
    finally { setLoading(false); }
  };

  // ── Cart path — single backend call submits the whole basket ─────
  const confirmCartRedeem = async () => {
    if (cart.length === 0 || !selectedDistributor) return;
    setLoading(true); setError("");
    try {
      const { data } = await API.post("/redemptions/cart-submit", {
        items: cart.map(l => ({ product_code: l.product_code, quantity: l.quantity })),
        distributor_id: selectedDistributor.db_code || "",
        distributor_name: selectedDistributor.name || "",
      });
      // Reuse the success screen — surface master_coupon as the visible code
      setResult({
        unique_id: data.master_coupon,
        master_coupon: data.master_coupon,
        lines: data.lines || [],
        dropped: data.dropped || [],
        points_redeemed: data.total_points || 0,
        balance_remaining: data.balance_remaining,
        is_cart: true,
      });
      clearCart();
      setStep("success");
      refreshUser();
    } catch (e) {
      setError(e.response?.data?.detail || "Cart redemption failed");
    } finally { setLoading(false); }
  };

  const [pickupConfirm, setPickupConfirm] = useState({ open: false, redemption: null, agreed: false, submitting: false });

  const openPickupConfirm = (redemption) => setPickupConfirm({ open: true, redemption, agreed: false, submitting: false });
  const closePickupConfirm = () => setPickupConfirm({ open: false, redemption: null, agreed: false, submitting: false });
  const submitPickupConfirm = async () => {
    if (!pickupConfirm.agreed || !pickupConfirm.redemption) return;
    setPickupConfirm(p => ({ ...p, submitting: true }));
    try {
      await API.put(`/redemptions/${pickupConfirm.redemption.unique_id}/confirm-pickup`, {
        acknowledged: true,
        acknowledged_at: new Date().toISOString(),
      });
      closePickupConfirm();
      loadRedemptions();
    } catch {
      setPickupConfirm(p => ({ ...p, submitting: false }));
    }
  };

  const copyCoupon = () => { if (result?.unique_id) { navigator.clipboard.writeText(result.unique_id); setCopied(true); setTimeout(() => setCopied(false), 2000); } };

  // Success screen
  if (step === "success" && result) return (
    <div className="text-center py-8 space-y-4" data-testid="redemption-success">
      <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
      <h2 className="text-xl font-bold text-gray-900">Redemption Successful!</h2>
      <div className="bg-[#E0F4F5] border-2 border-dashed border-[#008A99] rounded-2xl p-5 max-w-xs mx-auto">
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">{result.is_cart ? "Master Coupon" : "Coupon Code"}</p>
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg font-black text-[#008A99] tracking-wider" data-testid="success-coupon-code">{result.unique_id}</span>
          <button onClick={copyCoupon}>{copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-[#008A99]" />}</button>
        </div>
        {result.is_cart && result.points_redeemed != null && (
          <p className="text-[11px] text-gray-500 mt-2">{result.points_redeemed.toLocaleString()} pts redeemed across {(result.lines || []).length} reagent{(result.lines || []).length === 1 ? "" : "s"}</p>
        )}
      </div>
      {result.is_cart && (result.lines || []).length > 0 && (
        <div className="bg-white rounded-xl border p-4 max-w-xs mx-auto text-left text-xs space-y-1.5" data-testid="success-line-items">
          {result.lines.map((l, idx) => (
            <div key={idx} className="flex justify-between gap-2">
              <span className="text-gray-700 truncate">{l.product_name || l.product_code} × {l.quantity}</span>
              <span className="font-bold text-[#008A99] flex-shrink-0">{(l.line_points || 0).toLocaleString()} pts</span>
            </div>
          ))}
        </div>
      )}
      {result.is_cart && (result.dropped || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 max-w-xs mx-auto text-left text-xs space-y-1" data-testid="success-dropped">
          <p className="font-bold text-amber-800">The following items were skipped:</p>
          {result.dropped.map((d, idx) => (
            <p key={idx} className="text-amber-700">• {d.name || d.product_code} — {d.reason}</p>
          ))}
        </div>
      )}
      {selectedDistributor && (
        <div className="bg-white rounded-xl border p-4 max-w-xs mx-auto text-left text-sm">
          <p className="font-bold text-gray-900">{selectedDistributor.name}</p>
          <p className="text-xs text-gray-500 mt-1">{selectedDistributor.address}</p>
        </div>
      )}
      <p className="text-xs text-gray-500 max-w-xs mx-auto">Show this code at the distributor to collect your {result.is_cart ? "products" : "product"}</p>
      <div className="flex gap-3 justify-center pt-2">
        <button onClick={() => { setStep("history"); loadRedemptions(); }} className="px-5 py-2.5 border rounded-xl text-sm font-semibold">My Redemptions</button>
        <button onClick={() => navigate("/app")} className="px-5 py-2.5 bg-[#008A99] text-white rounded-xl text-sm font-semibold">Home</button>
      </div>
    </div>
  );

  // History tab
  if (step === "history") return (
    <div className="space-y-4" data-testid="redemption-history">
      <div className="flex items-center gap-3">
        <button onClick={() => setStep("segments")} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-bold text-gray-900">My Redemptions</h1>
      </div>
      {redemptions.length === 0 ? <p className="text-center text-gray-400 py-12 text-sm">No redemptions yet</p> :
        <div className="space-y-3">
          {redemptions.map((r, i) => (
            <div key={i} className="bg-white rounded-xl border p-4">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold text-sm">{toTitleCase(r.product_description || r.product_code || "")}</p>
                  {Array.isArray(r.lines) && r.lines.length > 0 && (
                    <p className="text-[11px] text-gray-500 mt-0.5">{r.lines.map(l => toTitleCase(l.product_description || l.product_code || "")).join(" · ")}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-0.5">{r.distributor_name || "—"}</p>
                  <p className="text-xs text-[#008A99] font-mono mt-1">{r.unique_id}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-red-500">-{r.points_redeemed} pts</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${r.status === "completed" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>{r.status === "completed" ? "Completed" : "Pending Pickup"}</span>
                </div>
              </div>
              {r.status !== "completed" && (
                r.otp_validated ? (
                  <button onClick={() => openPickupConfirm(r)} className="mt-3 w-full py-2 bg-green-50 text-green-600 rounded-lg text-xs font-semibold border border-green-200 hover:bg-green-100" data-testid={`order-received-btn-${r.unique_id}`}>
                    Order Received
                  </button>
                ) : (
                  <div className="mt-3 w-full py-2 bg-gray-50 text-gray-400 rounded-lg text-xs font-semibold border border-gray-200 flex items-center justify-center gap-1.5 cursor-not-allowed" data-testid={`order-received-locked-${r.unique_id}`}>
                    <Lock className="w-3 h-3" /> Awaiting delivery OTP verification
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      }

      {/* Order-received acknowledgment modal */}
      {pickupConfirm.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4 pb-24 sm:pb-4" data-testid="order-received-modal">
          <div className="bg-white w-full max-w-md rounded-2xl p-5 space-y-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <h3 className="text-base font-bold text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Confirm Order Received</h3>
              <button onClick={closePickupConfirm} className="p-1 rounded-lg hover:bg-gray-100" data-testid="order-received-close-btn">
                <span className="text-gray-400 text-lg leading-none">×</span>
              </button>
            </div>
            <div className="bg-gray-50 border rounded-xl p-3 text-xs text-gray-700">
              <p className="font-semibold text-gray-900 mb-1">{toTitleCase(pickupConfirm.redemption?.product_description || pickupConfirm.redemption?.product_code || "")}</p>
              <p className="text-gray-500">{pickupConfirm.redemption?.distributor_name || "—"}</p>
              <p className="text-[#008A99] font-mono mt-1 text-[11px]">{pickupConfirm.redemption?.unique_id}</p>
            </div>
            <p className="text-[12px] leading-relaxed text-gray-700" data-testid="order-received-legal-text">
              I hereby confirm that I have received all products against my order request in good and satisfactory condition.
              I have verified the quantity and specifications of the items received and acknowledge that there are no pending,
              missing, damaged, or disputed products. By submitting this confirmation, I accept the delivery as complete and final.
            </p>
            <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
              <input type="checkbox"
                checked={pickupConfirm.agreed}
                onChange={e => setPickupConfirm(p => ({ ...p, agreed: e.target.checked }))}
                className="mt-0.5 accent-[#008A99]"
                data-testid="order-received-agree-checkbox" />
              <span>I agree to the acknowledgment above.</span>
            </label>
            <div className="flex gap-2">
              <button onClick={closePickupConfirm} disabled={pickupConfirm.submitting}
                className="flex-1 py-2.5 border rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                data-testid="order-received-cancel-btn">
                Cancel
              </button>
              <button onClick={submitPickupConfirm} disabled={!pickupConfirm.agreed || pickupConfirm.submitting}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-[#008A99] hover:bg-[#007580] disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
                data-testid="order-received-submit-btn">
                {pickupConfirm.submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {pickupConfirm.submitting ? "Submitting…" : "Confirm Receipt"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="redeem-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {step !== "segments" && step !== "history" && (
            <button onClick={() => {
              if (step === "instruments") { setStep("segments"); setSelectedSegment(""); }
              else if (step === "reagents") { setStep("instruments"); setSelectedInstrument(null); }
              else if (step === "distributor" || step === "checking") { setStep("reagents"); setSelectedProduct(null); }
              else if (step === "confirm") setStep("distributor");
              else setStep("segments");
            }} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="redeem-back-btn"><ArrowLeft className="w-5 h-5" /></button>
          )}
          {step === "history" && <button onClick={() => setStep("segments")} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="redeem-back-btn"><ArrowLeft className="w-5 h-5" /></button>}
          <div><h1 className="text-xl font-bold text-gray-900">Redeem</h1><p className="text-xs text-gray-500">Balance: <span className="font-bold text-[#008A99]">{balance.toLocaleString()} pts</span></p></div>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2"><AlertCircle className="w-4 h-4 flex-shrink-0" />{error}</div>}

      {/* Step 0 — Segment Selection */}
      {step === "segments" && (
        <div className="space-y-3" data-testid="step-segments-redeem">
          {/* Test Item — 1 point sandbox redemption (visible to all customers) */}
          {(() => {
            const testItem = products.find(p => p.product_code === "TEST_REDEEM_1PT" || p.is_test_item);
            if (!testItem) return null;
            return (
              <button
                onClick={() => checkInventory(testItem)}
                disabled={!canRedeem(testItem)}
                className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 transition-all disabled:opacity-60"
                data-testid="redeem-test-item-tile"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
                    <Star className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="text-left min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-bold text-sm text-amber-900">Test Redemption</p>
                      <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-700 text-white">Sandbox</span>
                    </div>
                    <p className="text-[11px] text-amber-700">{testItem.name} · costs 1 pt</p>
                  </div>
                </div>
                <span className="text-sm font-black text-amber-800 flex-shrink-0 ml-2">1 pt</span>
              </button>
            );
          })()}
          <p className="text-sm font-semibold text-gray-700">Select a Segment</p>
          {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> :
            segments.length === 0 ? <p className="text-center text-gray-400 py-8 text-sm">No instruments available.</p> :
            <div className="space-y-2">
              {segments.map(seg => {
                const count = allInstruments.filter(i => i.segment === seg).length;
                const segLogo = resolveImg(segmentAssets[segmentSlug(seg)] || "");
                return (
                  <button key={seg}
                    onClick={() => { setSelectedSegment(seg); setStep("instruments"); }}
                    className="w-full flex items-center justify-between p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-sm transition-all"
                    data-testid={`redeem-segment-${seg}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-[#E0F4F5] flex items-center justify-center overflow-hidden">
                        {segLogo ? (
                          <img src={segLogo} alt={seg} className="w-full h-full object-contain"
                            onError={e => { e.currentTarget.style.display = "none"; }}
                            data-testid={`redeem-segment-logo-${seg}`} />
                        ) : (
                          <ShoppingBag className="w-5 h-5 text-[#008A99]" />
                        )}
                      </div>
                      <div className="text-left">
                        <p className="font-semibold text-sm text-gray-900">{toTitleCase(seg)}</p>
                        <p className="text-[10px] text-gray-400">{count} instrument{count === 1 ? "" : "s"}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          }
        </div>
      )}

      {/* Step 1 — Instrument Selection (within selected segment) */}
      {step === "instruments" && (
        <div className="space-y-3" data-testid="step-instruments">
          <div className="bg-[#E0F4F5] rounded-xl p-3 text-sm">
            <span className="text-gray-500">Segment: </span>
            <span className="font-semibold text-[#008A99]">{toTitleCase(selectedSegment)}</span>
          </div>
          <p className="text-sm font-semibold text-gray-700">Select an Instrument</p>
          {loading ? <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#008A99] mx-auto" /></div> :
            instrumentsForSegment.length === 0 ? <p className="text-center text-gray-400 py-8 text-sm">No instruments in this segment.</p> :
            <div className="space-y-2">
              {instrumentsForSegment.map(inst => (
                <button key={inst.instrument_code}
                  onClick={() => { setSelectedInstrument(inst); setStep("reagents"); }}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-xl border hover:border-[#008A99] hover:shadow-sm transition-all"
                  data-testid={`redeem-instrument-${inst.instrument_code}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-[#E0F4F5] flex items-center justify-center overflow-hidden">
                      {resolveImg(inst.image_url) ? (
                        <img src={resolveImg(inst.image_url)} alt={inst.instrument_code} className="w-full h-full object-contain"
                          onError={e => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <ShoppingBag className="w-5 h-5 text-[#008A99]" />
                      )}
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-sm text-gray-900">{toTitleCase(inst.name || inst.instrument_code || "")}</p>
                      <p className="text-[10px] text-gray-400">{toTitleCase(inst.segment || "")}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          }
        </div>
      )}

      {/* Step 2 — Reagent Selection */}
      {step === "reagents" && (
        <div className="space-y-3" data-testid="step-reagents-redeem">
          <div className="bg-[#E0F4F5] rounded-xl p-3 text-sm">
            <span className="text-gray-500">Instrument: </span>
            <span className="font-semibold text-[#008A99]">{toTitleCase(selectedInstrument?.name || selectedInstrument?.instrument_code || "")}</span>
          </div>
          {cartError && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-center gap-2" data-testid="cart-error">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{cartError}
            </div>
          )}
          <p className="text-sm font-semibold text-gray-700">Select a Reagent to Redeem</p>
          {reagentsForInstrument.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">No redeemable reagents mapped to this instrument.</p>
          ) : reagentsForInstrument.map(p => {
            const pts = getPointsRequired(p);
            const active = canRedeem(p);
            return (
              <div key={`${p.product_code}-${p.instrument_code}-${p.reagent_type}`} className={`bg-white rounded-xl border p-4 ${active ? "hover:border-[#008A99]" : "opacity-60"} transition-all`} data-testid="reward-card">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E0F4F5] to-gray-50 rounded-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <ReagentImg url={p.image_url} fallbackUrl={reagentIcon} alt={p.name} iconClass="w-7 h-7" testid={`reagent-image-${p.product_code}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm text-gray-900 truncate">{toTitleCase(p.name)}</p>
                    <p className="text-[10px] text-gray-500 mt-0.5 truncate">{p.product_code}{p.pack_size ? ` · ${p.pack_size}` : ""}</p>
                    {p.not_eligible ? (
                      <p className="text-[11px] font-semibold text-amber-600 mt-0.5" data-testid={`reagent-ineligible-${p.product_code}`}>{p.ineligible_reason || "Product is not eligible for discounting."}</p>
                    ) : (
                      <p className="text-xs font-bold text-[#008A99] mt-0.5">{pts.toLocaleString()} Points</p>
                    )}
                  </div>
                  {p.not_eligible ? (
                    <div className="text-right flex-shrink-0">
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-lg" data-testid={`reagent-not-eligible-badge-${p.product_code}`}>
                        <Lock className="w-3 h-3" /> Not eligible
                      </span>
                    </div>
                  ) : active ? (
                    (() => {
                      const line = findCartLine(p.product_code);
                      if (line) {
                        return (
                          <div className="flex items-center gap-1 bg-[#E0F4F5] rounded-xl px-1 py-1 flex-shrink-0" data-testid={`cart-qty-controls-${p.product_code}`}>
                            <button onClick={() => updateCartQty(p.product_code, -1)} className="w-7 h-7 rounded-lg bg-white text-[#008A99] flex items-center justify-center hover:bg-gray-50" data-testid={`cart-qty-minus-${p.product_code}`}>
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="w-6 text-center text-sm font-black text-[#008A99]" data-testid={`cart-qty-value-${p.product_code}`}>{line.quantity}</span>
                            <button onClick={() => updateCartQty(p.product_code, +1)} className="w-7 h-7 rounded-lg bg-[#008A99] text-white flex items-center justify-center hover:bg-[#006670]" data-testid={`cart-qty-plus-${p.product_code}`}>
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        );
                      }
                      return (
                        <button onClick={() => addToCart(p)} className="px-3 py-2 bg-[#008A99] text-white rounded-xl text-[11px] font-bold flex-shrink-0 flex items-center gap-1 hover:bg-[#006670]" data-testid={`add-to-cart-${p.product_code}`}>
                          <Plus className="w-3.5 h-3.5" /> Add
                        </button>
                      );
                    })()
                  ) : (
                    <div className="text-right flex-shrink-0"><Lock className="w-4 h-4 text-gray-300 mx-auto" /></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Step 2.5 — Cart Review */}
      {step === "cart" && (
        <div className="space-y-3 pb-24" data-testid="step-cart">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-gray-900">Your Cart</p>
            {cart.length > 0 && (
              <button onClick={clearCart} className="text-[11px] font-semibold text-red-500 hover:text-red-600" data-testid="cart-clear-btn">Clear all</button>
            )}
          </div>
          {cartError && (
            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex items-center gap-2">
              <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />{cartError}
            </div>
          )}
          {cart.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-dashed">
              <ShoppingCart className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Your cart is empty.</p>
              <button onClick={() => setStep("segments")} className="mt-3 text-[#008A99] text-xs font-bold hover:underline" data-testid="cart-browse-btn">Browse reagents →</button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {cart.map(line => (
                  <div key={line.product_code} className="bg-white rounded-xl border p-3 flex items-center gap-3" data-testid={`cart-line-${line.product_code}`}>
                    <div className="w-12 h-12 bg-[#E0F4F5] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <ReagentImg url={line.image_url} fallbackUrl={reagentIcon} alt={line.name} iconClass="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{toTitleCase(line.name)}</p>
                      <p className="text-[10px] text-gray-500 truncate">{line.product_code}{line.pack_size ? ` · ${line.pack_size}` : ""}</p>
                      <p className="text-[11px] font-bold text-[#008A99] mt-0.5">{line.unit_points.toLocaleString()} pts × {line.quantity} = <span className="font-black">{(line.unit_points * line.quantity).toLocaleString()} pts</span></p>
                    </div>
                    <div className="flex items-center gap-1 bg-[#E0F4F5] rounded-xl px-1 py-1 flex-shrink-0">
                      <button onClick={() => updateCartQty(line.product_code, -1)} className="w-7 h-7 rounded-lg bg-white text-[#008A99] flex items-center justify-center hover:bg-gray-50" data-testid={`cart-minus-${line.product_code}`}>
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="w-6 text-center text-sm font-black text-[#008A99]">{line.quantity}</span>
                      <button onClick={() => updateCartQty(line.product_code, +1)} className="w-7 h-7 rounded-lg bg-[#008A99] text-white flex items-center justify-center hover:bg-[#006670]" data-testid={`cart-plus-${line.product_code}`}>
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <button onClick={() => removeFromCart(line.product_code)} className="text-red-400 hover:text-red-600 p-1" data-testid={`cart-remove-${line.product_code}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-2xl border p-4 space-y-2 mt-3">
                <div className="flex justify-between text-xs text-gray-500"><span>Items</span><span className="font-semibold text-gray-800">{cartItemCount}</span></div>
                <div className="flex justify-between text-xs text-gray-500"><span>Your balance</span><span className="font-semibold text-gray-800">{balance.toLocaleString()} pts</span></div>
                <div className="border-t pt-2 flex justify-between items-center">
                  <span className="text-sm font-bold text-gray-700">Total</span>
                  <span className={`text-lg font-black ${cartTotal > balance ? "text-red-500" : "text-[#008A99]"}`} data-testid="cart-total-points">{cartTotal.toLocaleString()} pts</span>
                </div>
              </div>
              <button onClick={beginCheckout} disabled={cartTotal === 0 || cartTotal > balance} className="w-full py-3 bg-[#008A99] text-white rounded-xl text-sm font-bold hover:bg-[#006670] disabled:bg-gray-300" data-testid="cart-checkout-btn">
                Proceed to Checkout · {cartTotal.toLocaleString()} pts
              </button>
              <button onClick={() => setStep("segments")} className="w-full py-2 text-xs font-semibold text-gray-500 hover:text-[#008A99]" data-testid="cart-continue-shopping">+ Add more reagents</button>
            </>
          )}
        </div>
      )}

      {/* Distributor Selection — auto-picks the customer's most-transacted dealer
          (falls back to nearest by location, then any dealer). "Change" reveals
          the full radius-based list for users who want a different outlet. */}
      {step === "distributor" && (
        <div className="space-y-3" data-testid="step-distributor">
          <div className="bg-[#E0F4F5] rounded-xl p-3 text-sm flex justify-between items-center">
            <span>
              <span className="text-gray-500">Redeeming:</span>{" "}
              <span className="font-medium text-[#008A99]">
                {cart.length > 0 ? `${cartItemCount} item${cartItemCount === 1 ? "" : "s"} (${cart.length} reagent${cart.length === 1 ? "" : "s"})` : toTitleCase(selectedProduct?.name || "")}
              </span>
            </span>
            <span className="font-bold text-[#008A99]">{(cart.length > 0 ? cartTotal : getPointsRequired(selectedProduct)).toLocaleString()} pts</span>
          </div>

          {recLoading ? (
            <div className="text-center py-8" data-testid="recommended-dealer-loading">
              <Loader2 className="w-5 h-5 animate-spin text-[#008A99] mx-auto" />
              <p className="text-xs text-gray-400 mt-2">Picking the best dealer for you…</p>
            </div>
          ) : recError ? (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 flex items-center gap-2" data-testid="recommended-dealer-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />{recError}
            </div>
          ) : currentDealer && !showAllDealers ? (
            <div className="space-y-2" data-testid="recommended-dealer-card">
              <div className="rounded-2xl border-2 border-[#008A99] bg-white p-4 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  {dealerIdx > 0 ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#008A99] text-white" data-testid="dealer-source-alternative">
                      Alternative Dealer #{dealerIdx + 1}
                    </span>
                  ) : recommended?.source === "history" ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#008A99] text-white" data-testid="dealer-source-history">
                      <Star className="w-3 h-3" /> Your Frequent Dealer
                    </span>
                  ) : recommended?.source === "nearest" ? (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-600 text-white" data-testid="dealer-source-nearest">
                      <MapPin className="w-3 h-3" /> Nearest Dealer
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-gray-500 text-white" data-testid="dealer-source-fallback">
                      Suggested Dealer
                    </span>
                  )}
                  {currentDealer?.distance_km != null && (
                    <span className="text-[10px] font-bold text-[#008A99]">{currentDealer.distance_km} km away</span>
                  )}
                  {currentDealer?.redemption_count > 0 && (
                    <span className="text-[10px] font-bold text-gray-500">{currentDealer.redemption_count} past redemption{currentDealer.redemption_count === 1 ? "" : "s"}</span>
                  )}
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#E0F4F5] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#008A99]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-gray-900" data-testid="current-dealer-name">{currentDealer.name}</p>
                    {currentDealer.address && (
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{currentDealer.address}</p>
                    )}
                    {currentDealer.contact_number && (
                      <p className="text-[11px] text-gray-500 mt-0.5">{currentDealer.contact_number}</p>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStep("confirm")}
                  className="flex-1 py-3 rounded-xl bg-[#008A99] text-white text-sm font-bold hover:bg-[#006670]"
                  data-testid="continue-with-recommended-dealer"
                >
                  Continue with this dealer
                </button>
                {rankedDealers.length > 1 && (
                  <button
                    onClick={cycleDealer}
                    className="px-4 py-3 rounded-xl border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                    data-testid="change-dealer-btn"
                  >
                    Change
                  </button>
                )}
              </div>
              <button
                onClick={() => { setShowAllDealers(true); loadDistributors(12); }}
                className="w-full py-1.5 text-[11px] font-semibold text-gray-400 hover:text-[#008A99] transition-colors"
                data-testid="view-all-dealers-link"
              >
                View all nearby dealers
              </button>
            </div>
          ) : (
            <div className="space-y-2" data-testid="all-dealers-fallback">
              <div className="bg-white rounded-xl border p-4">
                <div className="flex items-center justify-between mb-2"><span className="text-xs font-semibold text-gray-500">Range</span><span className="text-sm font-bold text-[#008A99]">{radius} km</span></div>
                <input type="range" min="5" max="100" value={radius} onChange={e => handleRadiusChange(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#008A99]" />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1"><span>5km</span><span>50km</span><span>100km</span></div>
              </div>
              {distLoading ? <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#008A99] mx-auto" /></div> :
                distributors.length === 0 ? <p className="text-center text-gray-400 py-6 text-sm">No distributors within {radius}km. Increase range.</p> :
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {distributors.map((d, i) => (
                    <button key={i} onClick={() => { setSelectedDistributor(d); setStep("confirm"); }}
                      className="w-full text-left p-3 rounded-xl border hover:border-[#008A99] transition-all">
                      <div className="flex items-start gap-2">
                        <MapPin className="w-4 h-4 text-[#008A99] mt-0.5 flex-shrink-0" />
                        <div className="flex-1"><p className="font-medium text-sm">{d.name}</p><p className="text-xs text-gray-500">{d.address?.slice(0, 60)}...</p></div>
                        {d.distance_km != null && <span className="text-xs font-bold text-[#008A99]">{d.distance_km}km</span>}
                      </div>
                    </button>
                  ))}
                </div>
              }
              {recommended?.distributor && (
                <button onClick={() => setShowAllDealers(false)} className="w-full py-2 text-xs font-semibold text-[#008A99] hover:bg-[#E0F4F5] rounded-lg" data-testid="back-to-recommended-dealer">
                  ← Back to suggested dealer
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Confirm */}
      {step === "confirm" && (
        <div className="space-y-4" data-testid="step-confirm">
          <h3 className="font-semibold text-gray-900">Confirm Redemption</h3>
          {cart.length > 0 ? (
            <div className="bg-white border rounded-xl p-4 space-y-3 text-sm" data-testid="confirm-cart-summary">
              <div className="space-y-2">
                {cart.map(l => (
                  <div key={l.product_code} className="flex items-center gap-2" data-testid={`confirm-line-${l.product_code}`}>
                    <div className="w-9 h-9 bg-[#E0F4F5] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                      <ReagentImg url={l.image_url} fallbackUrl={reagentIcon} alt={l.name} iconClass="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-gray-800 truncate">{toTitleCase(l.name)}</p>
                      <p className="text-[11px] text-gray-500">{l.product_code} · {l.unit_points.toLocaleString()} × {l.quantity}</p>
                    </div>
                    <span className="text-sm font-bold text-gray-900 flex-shrink-0">{(l.unit_points * l.quantity).toLocaleString()} pts</span>
                  </div>
                ))}
              </div>
              <hr />
              <div className="flex justify-between"><span className="text-gray-500">Items</span><span className="font-medium">{cartItemCount} ({cart.length} reagent{cart.length === 1 ? "" : "s"})</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Distributor</span><span className="font-medium text-right">{selectedDistributor?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Total points</span><span className="font-bold text-red-500">{cartTotal.toLocaleString()} pts</span></div>
              <hr />
              <div className="flex justify-between"><span className="text-gray-500">Remaining balance</span><span className="font-bold text-[#008A99]">{(balance - cartTotal).toLocaleString()} pts</span></div>
            </div>
          ) : (
            <div className="bg-white border rounded-xl p-4 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Product</span><span className="font-medium">{toTitleCase(selectedProduct?.name || "")}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Points</span><span className="font-bold text-red-500">{getPointsRequired(selectedProduct)} pts</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Distributor</span><span className="font-medium">{selectedDistributor?.name}</span></div>
              <hr />
              <div className="flex justify-between"><span className="text-gray-500">Remaining Balance</span><span className="font-bold text-[#008A99]">{(balance - getPointsRequired(selectedProduct)).toLocaleString()} pts</span></div>
            </div>
          )}
          {error && <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700">{error}</div>}
          <button onClick={cart.length > 0 ? confirmCartRedeem : confirmRedeem} disabled={loading} className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50" data-testid="confirm-redeem-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Gift className="w-4 h-4" /> Confirm Redemption</>}
          </button>
        </div>
      )}

      {/* Floating cart bar — visible during browse steps when items are in cart */}
      {cart.length > 0 && ["segments", "instruments", "reagents"].includes(step) && (
        <button
          onClick={() => setStep("cart")}
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-30 bg-[#008A99] text-white rounded-full pl-4 pr-2 py-2 shadow-lg flex items-center gap-3 hover:bg-[#006670] active:scale-95 transition-all"
          data-testid="floating-cart-bar"
        >
          <ShoppingCart className="w-4 h-4" />
          <span className="text-xs font-bold">{cartItemCount} item{cartItemCount === 1 ? "" : "s"} · {cartTotal.toLocaleString()} pts</span>
          <span className="bg-white text-[#008A99] rounded-full px-3 py-1 text-[11px] font-black">View Cart →</span>
        </button>
      )}
    </div>
  );
}
